import io from 'socket.io-client';
import { ADD_FORM_TIME_ENTRIES } from 'ontraccr-common/lib/timeTracking/state/actionTypes';
import config from '../config';
import Permissions from '../auth/Permissions';
import {
  updateUserClockInState,
  updateUserClockOut,
  goOnBreak,
  endBreak,
  switchTask,
  handleMultipleTasks,
  getUsers,
} from '../users/state/users.actions';
import {
  getApprovals,
} from '../clock/state/clockin.actions';
import {
  getAssignedForms,
} from '../forms/state/forms.actions';
import {
  updateUserLimit,
} from '../settings/state/settings.actions';
import {
  addEvent,
  createPostFromSocket,
} from '../dashboard/LiveFeed/state/livefeed.actions';
import {
  getTeams,
} from '../teams/state/teams.actions';
import {
  DELETE_COSTCODE_BY_ID,
  CREATE_PROJECT,
  ARCHIVE_PROJECT,
  UPDATE_PROJECT,
  DELETE_PROJECT,
  ARCHIVE_COSTCODE,
  UPDATE_COSTCODE,
  CREATE_COSTCODE,
  CREATE_SHIFT,
  UPDATE_SHIFT,
  APPLY_SHIFT,
  DELETE_SHIFT,
  FORM_SOCKET_UPDATE,
  FORM_APPROVAL_SOCKET_UPDATE,
  DELETE_FORM,
  FORM_APPROVAL_DELETE,
  NEW_TAGS_FROM_SOCKET,
  UPDATE_EQUIPMENT_AVAILABILITY,
  CHECK_IN_EQUIPMENT,
  FORM_NUMBER_SOCKET_UPDATE,
  NEW_PROJECT_TAGS_FROM_SOCKET,
  NEW_PROJECT_GROUP_TAGS_FROM_SOCKET,
  NEW_BUCKET_TAGS_FROM_SOCKET,
  NEW_EQUIPMENT_TAGS_FROM_SOCKET,
  NEW_VENDOR_TAGS_FROM_SOCKET,
  NEW_CUSTOMER_TAGS_FROM_SOCKET,
  NEW_USER_TAGS_FROM_SOCKET,
  UPDATE_EQUIPMENT,
  UPDATE_PROJECT_SUB_CONTRACT,
} from '../state/actionTypes';
import store from '../state/store';

import {
  USER_CREATE_ACTION,
  TEAM_CREATE_ACTION,
} from '../dashboard/LiveFeed/liveFeed.constants';
import TimeTracking from '../state/timeTracking';
import Debouncer from '../helpers/Debouncer';
import { addToSocketList } from './state/socket.actions';

const { dispatch } = store;
const debouncer = new Debouncer();
const pendingTasksDebouncer = new Debouncer();
const pendingRefreshFormIds = new Set();
const PENDING_TASKS_REFRESH_MS = 500;

const flushPendingTasksRefresh = () => async (reduxDispatch) => {
  const formIds = Array.from(pendingRefreshFormIds);
  pendingRefreshFormIds.clear();
  if (!formIds.length) return;
  await reduxDispatch(TimeTracking.getPendingTimeEntries({ formIds }));
};

const hasQueuedPendingTasksRefresh = () => (
  !!store.getState().socket?.socketUserMap?.pendingTasks?.length
);

const queuePendingTasksRefresh = (formId, socketService) => {
  if (!formId) return;
  pendingRefreshFormIds.add(formId);

  if (socketService.pushToSocketList) {
    if (hasQueuedPendingTasksRefresh()) return;
    socketService.conditionalDispatch({
      actionCreator: flushPendingTasksRefresh,
      userId: 'pendingTasks',
    });
    return;
  }

  pendingTasksDebouncer.debounce(() => {
    const formIds = Array.from(pendingRefreshFormIds);
    pendingRefreshFormIds.clear();
    if (!formIds.length) return;
    dispatch(TimeTracking.getPendingTimeEntries({ formIds }));
  }, PENDING_TASKS_REFRESH_MS);
};

class SocketService {
  constructor() {
    this.connected = false;
    this.events = {};
  }

  connect({
    companyId,
    token,
    position,
    endpoint,
  }, rerender) {
    this.companyId = companyId;
    this.token = token;
    this.position = position;
    this.userId = window.sessionStorage.getItem('id');
    this.rerender = rerender;
    this.connectSocket = this.connectWrapper.bind(this);

    if (this.connected) return;
    this.socket = io(`${endpoint || config.baseURL}/${companyId}`, {
      query: {
        token,
      },
    });
    this.socket.on('connect', this.onConnect.bind(this));
    this.socket.on('error', (err) => {
      console.error('Socket err', err);
    });
    document.addEventListener('visibilitychange', () => {
      // If tab is no longer hidden then reconnect socket
      if (!document.hidden && this.socket.disconnected) {
        this.connectSocket();
      }
    });
  }

  connectWrapper() {
    if (this.socket.connected) return;
    this.socket.connect();
  }

  /**
   * Helper method to conditionally wrap dispatch calls with addToSocketList
   * @param {Function} actionCreator - The action creator function to dispatch
   * @param {*} data - Data to pass to the action creator
   * @param {string} userId - The user ID to pass to addToSocketList
   * @param {boolean} isApprovals - Whether the action is for approvals
   */
  conditionalDispatch({
    actionCreator,
    data,
    userId = data?.userId,
    isApprovals = false,
  }) {
    const dispatchAction = () => dispatch(actionCreator(data));

    if (this.pushToSocketList) {
      dispatch(addToSocketList(dispatchAction, userId, isApprovals));
    } else {
      dispatchAction();
    }
  }

  onConnect() {
    this.connected = true;

    this.socket.on('clockin', (data) => {
      this.conditionalDispatch({
        actionCreator: updateUserClockInState,
        data,
      });
    });

    this.socket.on('multipleTasks', (data) => {
      this.conditionalDispatch({
        actionCreator: handleMultipleTasks,
        data,
      });
    });

    this.socket.on('createdCostcode', (data) => {
      dispatch({
        type: CREATE_COSTCODE,
        payload: {
          costcodes: data,
        },
      });
    });

    this.socket.on('updatedCostcodes', (data) => {
      dispatch({
        type: UPDATE_COSTCODE,
        payload: {
          costcode: { id: data.id, ...data.payload },
        },
      });
    });

    this.socket.on('deletedCostcode', (data) => {
      dispatch({
        type: DELETE_COSTCODE_BY_ID,
        payload: {
          deletedId: data,
        },
      });
    });

    this.socket.on('archivedCostcode', (data) => {
      dispatch({
        type: ARCHIVE_COSTCODE,
        payload: {
          costcodeId: data.id,
          active: data.active,
        },
      });
    });

    this.socket.on('updateAvailability', (data) => {
      dispatch({
        type: UPDATE_EQUIPMENT_AVAILABILITY,
        payload: {
          equipmentId: data.id,
          availability: data.availability,
        },
      });
    });

    this.socket.on('checkInEquipment', (data) => {
      dispatch({
        type: CHECK_IN_EQUIPMENT,
        payload: {
          ...data,
        },
      });
    });

    this.socket.on('updatedProject', (data) => {
      dispatch({
        type: UPDATE_PROJECT,
        payload: {
          projectId: data.projectId,
          data: {
            details: data,
          },
        },
      });
    });

    this.socket.on('updatedProjectSubContract', (data) => {
      dispatch({
        type: UPDATE_PROJECT_SUB_CONTRACT,
        payload: {
          projectId: data.projectId,
          estimatedSubContractTotalValue: data.newEstimatedSubContractTotalValue,
        },
      });
    });

    this.socket.on('createdProject', (data) => {
      dispatch({
        type: CREATE_PROJECT,
        payload: {
          project: data,
        },
      });
    });

    this.socket.on('deletedProject', (data) => {
      dispatch({
        type: DELETE_PROJECT,
        payload: {
          id: data?.id || data,
        },
      });
    });

    this.socket.on('archivedProject', (data) => {
      dispatch({
        type: ARCHIVE_PROJECT,
        payload: {
          projectId: data.id,
          active: data.active,
        },
      });
    });

    this.socket.on(`updateSubmissions/${this.userId}`, () => {
      debouncer.debounce(() => this.conditionalDispatch({
        actionCreator: getApprovals,
        data: 'socket',
        isApprovals: true,
        userId: 'approvals',
      }), 5000);
    });

    this.socket.on(`updateTimeCards/${this.userId}`, (payload) => {
      this.conditionalDispatch({
        actionCreator: TimeTracking.updateTimeEntry,
        data: payload,
      });
    });

    this.socket.on('clockout', (data) => {
      const {
        userId,
        startTime,
        endTime,
        id: taskId,
        isAutoRounded,
      } = data;

      this.conditionalDispatch({
        actionCreator: updateUserClockOut,
        data: {
          userId, taskId, startTime, previousEnd: endTime, isAutoRounded,
        },
      });
    });

    this.socket.on('breakStart', (payload) => this.conditionalDispatch({
      actionCreator: goOnBreak,
      data: payload,
      userId: payload?.newEntry?.userId,
    }));
    this.socket.on('breakEnd', (payload) => this.conditionalDispatch({
      actionCreator: endBreak,
      data: payload,
      userId: payload?.newEntry?.userId,
    }));
    this.socket.on('switchTask', (payload) => this.conditionalDispatch({
      actionCreator: switchTask,
      data: payload,
      userId: payload?.newEntry?.userId,
    }));

    this.socket.on('userLimitUpdate', (payload) => dispatch(updateUserLimit(payload)));
    this.socket.on('newEvent', (payload) => {
      let hasUser = false;
      let hasTeam = false;
      payload.forEach((event) => {
        if (event.action === USER_CREATE_ACTION) hasUser = true;
        if (event.action === TEAM_CREATE_ACTION) hasTeam = true;
      });
      if (hasUser) {
        dispatch(getUsers(true));
      }
      if (hasTeam) {
        dispatch(getTeams());
      }
      dispatch(addEvent(payload));
    });

    this.socket.on(`newPost/${this.userId}`, (payload) => dispatch(createPostFromSocket(payload)));
    // Get public posts
    this.socket.on('newPost', (payload) => dispatch(createPostFromSocket(payload)));

    this.socket.on('createShift', this.dispatchShiftEvent(CREATE_SHIFT));
    this.socket.on('updateShift', this.dispatchShiftEvent(UPDATE_SHIFT));
    this.socket.on('applyShift', (data) => {
      dispatch({
        type: APPLY_SHIFT,
        payload: {
          appliedUser: data,
        },
      });
    });
    this.socket.on('deleteShift', this.dispatchShiftEvent(DELETE_SHIFT, 'shiftIds'));
    this.socket.on('formUpdate', (payload) => {
      dispatch({
        type: FORM_SOCKET_UPDATE,
        payload: {
          ...payload,
          currentUser: this.userId,
        },
      });
    });

    this.socket.on(`formApprovalUpdate/${this.userId}`, (approval) => {
      dispatch({
        type: FORM_APPROVAL_SOCKET_UPDATE,
        payload: {
          approval,
        },
      });
    });

    this.socket.on('formNumberUpdates', (payload) => {
      dispatch({
        type: FORM_NUMBER_SOCKET_UPDATE,
        payload,
      });
    });

    this.socket.on(`formEditRequest/${this.userId}`, () => {
      dispatch(getAssignedForms());
    });
    // TODO: Add position support
    // Backend sends position ID, but this.position is the name of the position
    this.socket.on(`formApprovalUpdate/${this.position}`, (approval) => {
      dispatch({
        type: FORM_APPROVAL_SOCKET_UPDATE,
        payload: {
          approval,
        },
      });
    });

    this.socket.on('formDelete', (payload) => {
      dispatch({
        type: DELETE_FORM,
        payload,
      });
    });

    this.socket.on('formApprovalDelete', (payload) => {
      dispatch({
        type: FORM_APPROVAL_DELETE,
        payload,
      });
    });

    this.socket.on(`newBoardTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newProjectTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_PROJECT_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newProjectGroupTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_PROJECT_GROUP_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newBucketTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_BUCKET_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newEquipmentTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_EQUIPMENT_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newVendorTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_VENDOR_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newCustomerTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_CUSTOMER_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on(`newUserTags/${this.userId}`, (newTags = []) => {
      dispatch({
        type: NEW_USER_TAGS_FROM_SOCKET,
        payload: {
          newTags,
        },
      });
    });

    this.socket.on('updateEquipment', (data) => {
      dispatch({
        type: UPDATE_EQUIPMENT,
        payload: {
          id: data.id,
          newData: {
            currentHours: data.currentHours,
            totalLifetimeHours: data.totalLifetimeHours,
            currentMileage: data.currentMileage,
            totalLifetimeMileage: data.totalLifetimeMileage,
          },
        },
      });
    });

    this.socket.on('refreshPendingTasks', ({ formId } = {}) => {
      queuePendingTasksRefresh(formId, this);
    });

    this.socket.on('addFormTimeEntries', (data) => {
      this.conditionalDispatch({
        actionCreator: (payload) => ({
          type: ADD_FORM_TIME_ENTRIES,
          payload,
        }),
        data,
      });
    });

    this.socket.on('removePendingTimeEntriesByForm', (data) => {
      this.conditionalDispatch({
        actionCreator: TimeTracking.removePendingTimeEntriesByForm,
        data,
      });
    });

    this.socket.on('dispatchReduxEvent', dispatch);

    this.socket.on('permissions/rename', ({ renames }) => {
      if (renames?.length) {
        Permissions.rename(renames);
      }
    });

    this.socket.on(`permissions/${this.position}`, ({
      action,
      permission,
      permissions,
      newPosition,
      userId,
    }) => {
      switch (action) {
        case 'add':
          Permissions.add(permission);
          if (userId !== Permissions.id) this.rerender();
          break;
        case 'delete':
          Permissions.delete(permission);
          if (userId !== Permissions.id) this.rerender();
          break;
        case 'reset':
          if (Permissions.reload(permissions)) {
            if (userId !== Permissions.id) this.rerender();
          }
          break;
        case 'reassign':
          if (Permissions.reload(permissions)) {
            this.rerender();
            // eslint-disable-next-line no-undef
            window.sessionStorage.setItem('position', newPosition);
          }
          break;
        default:
          console.log(`Invalid permssion action received: ${action}`);
      }
    });

    Object.keys(this.events).forEach((event) => this.socket.on(event, this.events[event]));
  }

  register(event, callback) {
    this.events[event] = callback;
  }

  dispatchShiftEvent(type, key = 'shifts') {
    return ({ [key]: data = [], users = [], fromUpdate }) => {
      const hasScheduleWrite = Permissions.has('SCHEDULE_WRITE');
      if (!hasScheduleWrite && !users.includes(this.userId)) return;
      if (hasScheduleWrite && fromUpdate) return;
      dispatch({
        type,
        payload: {
          [key]: data,
        },
      });
    };
  }

  startEditingBoardCard(cardId, userId) {
    this.socket.emit('startEditingBoardCard', { cardId, userId });
  }

  stopEditingBoardCard(cardId, userId) {
    this.socket.emit('stopEditingBoardCard', { cardId, userId });
  }

  listenForBoardCardEditing(cardId, callback) {
    this.socket.on(`editingBoardCard/${cardId}`, callback);
  }

  stopListeningForBoardCardEditing(cardId) {
    this.socket.off(`editingBoardCard/${cardId}`);
  }

  enableSocketList() {
    this.pushToSocketList = true;
  }

  disableSocketList() {
    this.pushToSocketList = false;
  }
}

export default new SocketService();
